export const brands = ["onestop", "indo", "rodenstock", "optovision"] as const;
export type Brand = (typeof brands)[number];

export const brandThemes = {
  onestop: "theme-default",
  indo: "theme-indo",
  rodenstock: "theme-rodenstock",
  optovision: "theme-default",
} as const;

export const brandLabels = {
  onestop: "Onestop",
  indo: "Indo",
  rodenstock: "Rodenstock",
  optovision: "Optovision",
} as const;

export const brandLogos = {
  indo: "@/assets/theme/images/logo-indo.svg",
  rodenstock: "@/assets/theme/images/logo-rodenstock.svg",
  optovision: "@/assets/theme/images/logo-optovision.svg",
} as const;

export const defaultBrand: Brand = "onestop";

type BrandInfo = {
  brand: string;
  name: string;
  logoLarge: string;
  logoSmall: string;
  theme: string;
};
export const brandsConfig = {
  onestop: {
    brand: "onestop",
    name: "Onestop",
    logoLarge: "onestop",
    logoSmall: "onestop",
    theme: "theme-default",
  },
  indo: {
    brand: "indo",
    name: "Indo",
    logoLarge: "indo",
    logoSmall: "indo-image",
    theme: "theme-indo",
  },
  rodenstock: {
    brand: "rodenstock",
    name: "Rodenstock",
    logoLarge: "rodenstock",
    logoSmall: "rodenstock-image",
    theme: "theme-rodenstock",
  },
  optovision: {
    brand: "optovision",
    name: "Optovision",
    logoLarge: "optovision",
    logoSmall: "optovision-image",
    theme: "theme-default",
  },
} as const satisfies Record<string, BrandInfo>;

/**
 * Extrae el identificador de marca desde TEXTO_ORGANIZACION
 * Ejemplos:
 * - "Indo Optical S.L." → "indo"
 * - "Rodenstock Iberia S.L." → "rodenstock"
 * - "Optovision" → "optovision"
 * - "OptVision España" → "optovision"
 */
export function extractBrandFromOrganization(textoOrganizacion: string): string {
  if (!textoOrganizacion) return "";

  const text = textoOrganizacion.toLowerCase();

  // Buscar keywords conocidas
  if (text.includes("indo")) {
    return "indo";
  }
  if (text.includes("rodenstock")) {
    return "rodenstock";
  }
  if (text.includes("optovision") || text.includes("optvision")) {
    return "optovision";
  }

  // Si no encuentra nada conocido, extraer la primera palabra
  const firstWord = textoOrganizacion.trim().split(/\s+/)[0].toLowerCase();
  const result = firstWord.replace(/[^a-z0-9]/g, "");
  return result;
}

/**
 * Normaliza un identificador de marca para usar en URLs
 * Elimina caracteres especiales y convierte a minúsculas
 */
export function normalizeBrandId(brandId: string): string {
  return brandId.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Genera configuración de marca dinámica desde AreaVentaListaAreaCliente
 * Si no existe configuración específica, usa fallbacks de Indo
 */
export function getDynamicBrandConfig(
  orgVenta: string,
  textoOrganizacion: string
): BrandInfo {
  const brandId = normalizeBrandId(extractBrandFromOrganization(textoOrganizacion));

  // Si existe configuración predefinida, usarla
  if (brandId && brandsConfig[brandId as keyof typeof brandsConfig]) {
    const config = brandsConfig[brandId as keyof typeof brandsConfig];
    return config;
  }

  // Fallback: crear configuración dinámica basada en Indo
  const fallbackConfig = {
    brand: brandId || `org-${orgVenta}`,
    name: textoOrganizacion.split(/\s+/)[0] || "Marca",
    logoLarge: "indo", // Fallback a logo de Indo
    logoSmall: "indo-image", // Fallback a logo pequeño de Indo
    theme: "theme-indo", // Fallback a tema de Indo
  };
  return fallbackConfig;
}

/**
 * Genera las URLs del menú para una marca específica
 */
export function getBrandMenuUrls(brandId: string) {
  // Marketing solo se muestra cuando VITE_MODO_TEST=true (independiente de optovision).
  const isTestMode = import.meta.env.VITE_MODO_TEST === "true";
  return [
    { title: "facturacion", icon: "check-document", to: `/theme/${brandId}/facturacion` },
    { title: "formacion", icon: "graduation-cap", to: `/theme/${brandId}/formacion` },
    ...(isTestMode
      ? [{ title: "marketing", icon: "thumbs-up", to: `/theme/${brandId}/marketing` }]
      : []),
  ];
}
